<script setup lang="ts">
import { trackEvent } from '@@/bits/analytics'
import { appLink } from '@@/bits/app_link'
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { isInIframe } from '@@/bits/global'
import { escapeHtml, html } from '@@/bits/html_parser'
import { __ } from '@@/bits/intl'
import { navigateTo } from '@@/bits/location'
import {
  audienceLabelForPersona,
  enterPreview,
  exitPreview,
  isNamedPersona,
  splitBannerCopy,
  type ViewAsPreviewPersona,
} from '@@/bits/surface_view_as_preview'
import { OzBaseButtonHrefMode } from '@@/library/v4/components/OzBaseButton.vue'
import OzContainedButton, {
  OzContainedButtonColorScheme,
  OzContainedButtonSizePreset,
} from '@@/library/v4/components/OzContainedButton.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import OzIconButton, {
  OzIconButtonColorScheme,
  OzIconButtonSizePreset,
} from '@@/library/v4/components/OzIconButton.vue'
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'
import OzInput from '@@/library/v4/components/OzInput.vue'
import OzPadletCrane, { OzPadletCraneSizePreset } from '@@/library/v4/components/OzPadletCrane.vue'
import OzPadletLogo, { OzPadletLogoColorScheme } from '@@/library/v4/components/OzPadletLogo.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from '@@/library/v4/components/OzPlainButton.vue'
import { AccessibilitySetting, useAccessibilitySettingsStore } from '@@/pinia/accessibility_settings_store'
import type { User } from '@@/types'
import AccessRequestBackgroundIllustration from '@@/vuecomponents/AccessRequestBackgroundIllustration.vue'
import AccessRequestFooter from '@@/vuecomponents/AccessRequestFooter.vue'
import SurfaceButtonJoint from '@@/vuecomponents/SurfaceButtonJoint.vue'
import { useHighContrastMode } from '@@/vuecomposables/useHighContrastMode'
import { usePreferredDark, useResizeObserver } from '@vueuse/core'
import { offset, shift, type ComputePositionConfig } from '@floating-ui/vue'
import { computed, defineAsyncComponent, onMounted, ref } from 'vue'

const AccessibilitySettingsModal = defineAsyncComponent(() => import('@@/vuecomponents/AccessibilitySettingsModal.vue'))
const UserMenuPopover = defineAsyncComponent(() => import('@@/vuecomponents/UserMenuPopover.vue'))
const OzPopover = defineAsyncComponent(() => import('@@/library/v4/components/OzPopover.vue'))
const ViewAsPreviewPersonaMenu = defineAsyncComponent(() => import('@@/vuecomponents/ViewAsPreviewPersonaMenu.vue'))

export interface CtaButton {
  text: string
  url: string
  colorScheme: OzContainedButtonColorScheme
  width?: number
  endIcon?: string
  endIconSize?: number
}

export interface Props {
  title: string
  currentUser: User
  description?: string
  ctaButtons?: CtaButton[]
  helperHtml?: string

  // Password protected padlets
  showPasswordInput?: boolean
  passwordSubmitUrl?: string
  passwordErrorText?: string
  passwordValue?: string

  viewAsPreviewPersonas?: ViewAsPreviewPersona[] | null
  isWhiteboard?: boolean
  /** Server-resolved `view_as` persona key while previewing; nil when the preview does not resolve. */
  viewAsPersona?: string | null
}

/** dark: classes are not working as intended, so we have to manually set styling based on this dark mode value */
const isDark = usePreferredDark()
const accessibilitySettingsStore = useAccessibilitySettingsStore()

const {
  description,
  ctaButtons = [],
  currentUser,
  showPasswordInput = false,
  passwordSubmitUrl,
  helperHtml,
  passwordErrorText,
  passwordValue,
  viewAsPreviewPersonas = null,
  isWhiteboard = false,
  viewAsPersona = null,
} = defineProps<Props>()

// #region View-as preview notice
// Only a persona resolving to `none` can reach this page; every other previewable role passes
// `padlet_read`, so the board renders and the board's banner handles the notice.
const previewedPersonaKey = computed((): string | undefined => {
  if (!isAppUsing('viewAsPreview') || viewAsPersona == null) return undefined
  return viewAsPersona
})

const xShowPreviewNotice = computed((): boolean => isAppUsing('viewAsPreview') && viewAsPersona != null)

const selectedPersona = computed((): ViewAsPreviewPersona | undefined =>
  viewAsPreviewPersonas?.find((persona) => persona.key === previewedPersonaKey.value),
)

const canSwitchPersona = computed((): boolean => (viewAsPreviewPersonas?.length ?? 0) > 1)

const noticeParts = computed((): { prefix: string; trigger: string; suffix: string } | undefined => {
  if (!xShowPreviewNotice.value) return undefined

  const persona = selectedPersona.value
  if (persona != null) {
    const audience = audienceLabelForPersona(persona)
    const name = isNamedPersona(persona) ? html`<bdi>${audience}</bdi>`.toString() : escapeHtml(audience)
    const translated = __('Viewing as <b>%{name} with no access</b>', { name })
    return splitBannerCopy(translated)
  }

  return splitBannerCopy(__('Viewing with <b>no access</b>'))
})

const xPersonaMenuOpen = ref(false)
const personaTriggerEl = ref<HTMLElement | null>(null)

const popoverConfig: ComputePositionConfig = {
  placement: 'bottom',
  strategy: 'fixed',
  middleware: [offset({ mainAxis: 4 }), shift({ padding: 8 })],
}

function togglePersonaMenu(): void {
  xPersonaMenuOpen.value = !xPersonaMenuOpen.value
}

function selectPreviewPersona(persona: ViewAsPreviewPersona): void {
  xPersonaMenuOpen.value = false
  if (persona.key === previewedPersonaKey.value) return
  enterPreview(persona.key)
}

onMounted(() => {
  const persona = selectedPersona.value
  if (persona == null) return
  trackEvent('Surface view as', 'Started view as preview', null, null, {
    persona: persona.key,
    role: persona.role,
  })
})
// #endregion

// #region Open in the Padlet app
/**
 * The app is usually already signed in as the right user, so on a phone it is the shortest
 * route to a padlet this browser cannot open.
 *
 * Same test as the surface header's own button (`SurfaceHeaderMobile.vue`): the device runs
 * iOS or Android, and this is not already the app. The flags are user-agent based, so the
 * button follows the device rather than the window — an iPad Pro in landscape is 1366px wide
 * and is exactly the kind of device that has the app.
 */
const xOpenInAppButton = computed(
  () => isAppUsing('openInAppOnAccessGate') && !device.app && (device.ios || device.android),
)

const handleOpenInAppClick = () => {
  trackEvent('Access Request', 'Clicked open in app button')
  // The password gate also renders inside an embed iframe. A universal link has to be handed to a
  // top-level context to leave the embed at all, so open a new tab there — the same guard both
  // other open-in-app call sites use.
  navigateTo(appLink(), isInIframe() ? { target: '_blank' } : {})
}
// #endregion

// #region CTA buttons
const handleCTAButtonClick = (url: string) => {
  // Check if this is a request access URL which needs POST form submission
  if (url.includes('_/access')) {
    // Extract wall_id from query parameters
    const urlObj = new URL(url, window.location.origin)
    const wallId = urlObj.searchParams.get('wall_id')

    if (wallId) {
      trackEvent('Access Request', 'Clicked request access button', wallId)

      // Submit as POST form with wall_id
      const form = document.createElement('form')
      form.method = 'POST'
      form.action = '/_/access'

      // Add wall_id input
      const wallIdInput = document.createElement('input')
      wallIdInput.type = 'hidden'
      wallIdInput.name = 'wall_id'
      wallIdInput.value = wallId
      form.appendChild(wallIdInput)

      // Add CSRF token
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      if (csrfToken) {
        const csrfInput = document.createElement('input')
        csrfInput.type = 'hidden'
        csrfInput.name = 'authenticity_token'
        csrfInput.value = csrfToken
        form.appendChild(csrfInput)
      }

      document.body.appendChild(form)
      form.submit()
      return
    }
  }

  trackEvent('Access Request', 'Clicked CTA button', url)

  // For all other URLs, use regular navigation
  navigateTo(url)
}
// #endregion

// #region Password form
const password = ref(passwordValue || '')
const handlePasswordSubmit = () => {
  if (passwordSubmitUrl && password.value) {
    trackEvent('Access Request', 'Submitted password')

    // Submit password via form POST
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = passwordSubmitUrl

    // Add password input
    const passwordInput = document.createElement('input')
    passwordInput.type = 'hidden'
    passwordInput.name = 'password'
    passwordInput.value = password.value
    form.appendChild(passwordInput)

    // Add CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    if (csrfToken) {
      const csrfInput = document.createElement('input')
      csrfInput.type = 'hidden'
      csrfInput.name = 'authenticity_token'
      csrfInput.value = csrfToken
      form.appendChild(csrfInput)
    }

    document.body.appendChild(form)
    form.submit()
  }
}
// #endregion

// #region User menu
const xUserMenu = ref(false)
const showUserMenu = () => {
  xUserMenu.value = true
}
const hideUserMenu = () => {
  xUserMenu.value = false
}

const avatarButtonRef = ref<InstanceType<typeof OzPlainButton> | null>(null)
const avatarButtonAnchor = computed(() => avatarButtonRef.value?.$el)
const avatarButtonBackgroundColor = computed(() => {
  if (xUserMenu.value) {
    // When menu is open, use a more visible background
    return isDark.value ? 'rgba(255, 255, 255, 0.16)' : 'rgba(0, 0, 0, 0.08)'
  }
  return undefined
})
// #endregion

// #region Registered user specific buttons
const handlePadletCraneClick = () => {
  trackEvent('Access Request', 'Clicked padlet crane button')
}
const handlePadletLogoClick = () => {
  trackEvent('Access Request', 'Clicked padlet logo button')
}

const handleSettingsButtonClick = () => {
  trackEvent('Access Request', 'Clicked settings button')
}
const handleAvatarButtonClick = () => {
  trackEvent('Access Request', 'Clicked avatar button')
  showUserMenu()
}

const user = computed(() => currentUser)
useHighContrastMode(user)

// #region Description line count
const descriptionRef = ref<HTMLParagraphElement | null>(null)
const isDescriptionMultiLine = ref(false)

useResizeObserver(descriptionRef, () => {
  if (!descriptionRef.value) return
  const el = descriptionRef.value
  const lineHeight = parseFloat(getComputedStyle(el).lineHeight)
  isDescriptionMultiLine.value = el.scrollHeight > Math.round(lineHeight * 1.5)
})
// #endregion
</script>

<template>
  <main
    :class="[
      'h-[100dvh] relative overflow-hidden',
      isDark ? 'bg-dark-ui-200 dark-theme' : 'bg-[#F7EDFF] tablet-portrait:bg-[#F7EDFF] light-theme',
    ]"
  >
    <div
      v-if="noticeParts"
      role="status"
      data-testid="accessRequestViewAsPreviewNotice"
      :class="[
        'relative z-30 flex items-center justify-between gap-3 px-4 py-2 min-w-0',
        isDark ? 'bg-dark-ui-100 text-light-text-100' : 'bg-light-ui-100 text-dark-text-100',
      ]"
    >
      <div class="flex items-center gap-1 min-w-0 flex-1">
        <span
          v-if="noticeParts.prefix"
          class="text-body-small font-normal whitespace-nowrap shrink-0 hidden tablet-portrait:inline"
          data-testid="accessRequestViewAsPreviewNoticePrefix"
          >{{ noticeParts.prefix }}</span
        >
        <div ref="personaTriggerEl" class="shrink min-w-0">
          <OzBaseButton
            v-if="canSwitchPersona"
            :class="[
              'max-w-full text-start tablet-portrait:flex tablet-portrait:items-center',
              'text-body-small font-semibold',
              'bg-transparent p-0 border-0',
              isDark ? 'text-light-text-100' : 'text-dark-text-100',
              'hover-hover:hover:underline',
              'focus-visible:underline',
            ]"
            aria-haspopup="menu"
            :aria-expanded="xPersonaMenuOpen"
            data-testid="accessRequestViewAsPreviewNoticeTrigger"
            @click="togglePersonaMenu"
          >
            <span
              class="whitespace-break-spaces tablet-portrait:truncate tablet-portrait:whitespace-nowrap"
              v-html="noticeParts.trigger"
            />
            <span class="ms-0.5 inline-flex shrink-0" data-testid="accessRequestViewAsPreviewNoticeChevron">
              <OzIcon name="chevron_down" :size="12" />
            </span>
          </OzBaseButton>
          <span
            v-else
            :class="[
              'max-w-full text-body-small font-semibold',
              'whitespace-break-spaces tablet-portrait:block tablet-portrait:truncate tablet-portrait:whitespace-nowrap',
              isDark ? 'text-light-text-100' : 'text-dark-text-100',
            ]"
            data-testid="accessRequestViewAsPreviewNoticeTrigger"
            v-html="noticeParts.trigger"
          />
        </div>
        <span
          v-if="noticeParts.suffix"
          class="text-body-small font-normal whitespace-nowrap shrink-0 hidden tablet-portrait:inline"
          data-testid="accessRequestViewAsPreviewNoticeSuffix"
          >{{ noticeParts.suffix }}</span
        >
      </div>
      <OzBaseButton
        :class="[
          'shrink-0 flex items-center justify-center',
          'h-8 px-3 rounded-lg',
          'bg-transparent',
          'text-body-small font-semibold',
          isDark ? 'text-light-text-100' : 'text-dark-text-100',
          'hover-hover:hover:underline',
          'focus-visible:underline',
        ]"
        :aria-label="__('Done')"
        data-testid="accessRequestViewAsPreviewDoneButton"
        @click="exitPreview"
      >
        {{ __('Done') }}
      </OzBaseButton>

      <OzPopover
        v-if="canSwitchPersona && personaTriggerEl && xPersonaMenuOpen"
        :anchor="personaTriggerEl"
        :config="popoverConfig"
        z-index-class="z-dropdown"
        @scrim-esc="xPersonaMenuOpen = false"
        @scrim-click="xPersonaMenuOpen = false"
      >
        <ViewAsPreviewPersonaMenu
          :personas="viewAsPreviewPersonas ?? []"
          :selected-key="previewedPersonaKey"
          :is-whiteboard="isWhiteboard"
          :dark-mode="isDark"
          test-id="accessRequestViewAsPreviewPersonaMenu"
          class="shadow-elevation-2 w-64"
          @exit="xPersonaMenuOpen = false"
          @select="selectPreviewPersona"
        />
      </OzPopover>
    </div>

    <!-- Padlet Logo or Crane, currently only shown in desktop view based on design -->
    <div class="absolute hidden tablet-portrait:block top-6 start-6 z-20">
      <OzPadletCrane
        v-if="currentUser.registered_at"
        :size-preset="OzPadletCraneSizePreset.W28px"
        :href-mode="OzBaseButtonHrefMode.NewTab"
        @click="handlePadletCraneClick"
      />
      <OzPadletLogo
        v-else
        :color-scheme="OzPadletLogoColorScheme.Text300"
        :x-crane-icon="false"
        :href-mode="OzBaseButtonHrefMode.NewTab"
        @click="handlePadletLogoClick"
      />
    </div>

    <div class="absolute flex top-6 end-6 z-20 gap-3 items-center">
      <OzContainedButton
        v-if="xOpenInAppButton"
        :class="[
          'max-w-[calc(100vw-6rem)] truncate',
          isDark
            ? 'bg-canary-900 text-canary-100 hhover:bg-canary-800'
            : 'bg-grape-200 text-grape-800 hhover:bg-grape-300',
        ]"
        :text="__('Open in app')"
        :aria-label="__('Open in Padlet app')"
        :color-scheme="OzContainedButtonColorScheme.Bare"
        :size-preset="OzContainedButtonSizePreset.H32px"
        :dark-mode="isDark"
        data-testid="accessRequestOpenInAppButton"
        @click="handleOpenInAppClick"
      />

      <!-- Settings and Avatar buttons for registered users -->
      <div v-if="currentUser.registered_at" class="hidden tablet-portrait:flex gap-3 items-center">
        <OzIconButton
          :icon-name="'settings'"
          :label="__('Settings')"
          :color-scheme="OzIconButtonColorScheme.Secondary"
          :size-preset="OzIconButtonSizePreset.H32px"
          :href-mode="OzBaseButtonHrefMode.NewTab"
          :href="'/dashboard/settings'"
          @click="handleSettingsButtonClick"
        />
        <div class="relative">
          <OzPlainButton
            ref="avatarButtonRef"
            :class="[
              'p-1! border-box size-8 rounded-xl focus-visible:ring-2',
              xUserMenu ? 'shadow-elevation-2' : 'hhover:bg-tangerine-100!',
              {
                'focus-visible:ring-grape-500': !isDark,
                'focus-visible:ring-canary-500': isDark,
              },
            ]"
            :style="{
              backgroundColor: avatarButtonBackgroundColor || '#FFF5EF',
            }"
            :size-preset="OzPlainButtonSizePreset.Bare"
            :color-scheme="OzPlainButtonColorScheme.Bare"
            :aria-label="__('Open user menu')"
            @click="handleAvatarButtonClick"
          >
            <div class="rounded-lg h-6 w-6 overflow-hidden">
              <img v-if="currentUser.avatar" :src="currentUser.avatar" :alt="__('User avatar')" class="h-6 w-6" />
            </div>
          </OzPlainButton>
          <SurfaceButtonJoint
            v-if="xUserMenu"
            :class="xUserMenu && 'shadow-elevation-2'"
            :fill-color="avatarButtonBackgroundColor || '#FFF5EF'"
            dir="bottom"
          />
        </div>
      </div>
    </div>

    <!-- Background illustration -->
    <AccessRequestBackgroundIllustration
      class="hidden tablet-portrait:block"
      :type="currentUser.registered_at ? 'LoggedIn' : 'NotLoggedIn'"
    />

    <div
      class="mt-[80px] mx-7 tablet-portrait:mx-8 tablet-portrait:mt-[16vh] tablet-portrait:ms-[4vw] desktop:ms-[120px] desktop:mt-[clamp(80px,18vh,268px)] relative z-10"
    >
      <div class="max-w-[680px]">
        <!-- Title - supports HTML for styled text -->
        <h1
          :class="[
            'text-heading-2 tablet-portrait:text-login-heading-1 m-0',
            isDark ? 'text-light-text-100' : 'text-dark-text-100',
          ]"
          :style="{ letterSpacing: '-0.0013125em' }"
          v-html="title"
        />

        <!-- Description -->
        <p
          v-if="description"
          ref="descriptionRef"
          id="description"
          :class="[
            'text-body-small tablet-portrait:text-login-body text-balance tablet-portrait:mt-3 mb-5',
            isDescriptionMultiLine ? 'tablet-portrait:mb-10' : 'tablet-portrait:mb-5',
            isDark ? 'text-light-text-100' : 'text-dark-text-100',
          ]"
          v-html="description"
        />

        <!-- Password Input -->
        <div v-if="showPasswordInput" class="mb-5 tablet-portrait:mb-10">
          <form @submit.prevent="handlePasswordSubmit" class="flex gap-3 items-start">
            <OzInput
              v-model="password"
              type="password"
              :placeholder="'*****'"
              :class="['flex-1 max-w-60 tablet-portrait:max-w-80', isDark ? 'bg-dark-ui-100' : 'bg-light-ui-100']"
              autofocus
              :aria-label="__('Password')"
              :valid="passwordErrorText ? 'no' : 'unknown'"
              :validation-message="passwordErrorText || ''"
            />
            <OzContainedButton
              type="submit"
              class="tablet-portrait:w-20"
              :text="__('Go')"
              :aria-label="__('Submit')"
              :color-scheme="OzContainedButtonColorScheme.Primary"
              :disabled="!password"
            />
          </form>
        </div>

        <!-- CTA Buttons -->
        <div
          v-if="ctaButtons.length > 0"
          :class="[
            'flex flex-col tablet-portrait:flex-row gap-3 tablet-portrait:gap-4 mb-5',
            isDescriptionMultiLine ? 'tablet-portrait:mb-10' : 'tablet-portrait:mb-5',
          ]"
          data-testid="accessRequestCTAContainer"
        >
          <!--
            The label is a full sentence in several languages — Ukrainian's "Log in as an
            authorized user" is nearly three times the English width. The button keeps its
            fit-content width so English is unchanged, and the two props below let a label
            that no longer fits wrap instead of running past the page margin:
            `should-truncate-text` off drops the `nowrap` that pins the button's minimum width
            to the whole line, and `auto-height` swaps the fixed height for a minimum one so
            the second line has somewhere to go.
          -->
          <OzContainedButton
            v-for="button in ctaButtons"
            :key="button.text"
            :color-scheme="button.colorScheme"
            :text="button.text"
            :should-truncate-text="false"
            auto-height
            @click="handleCTAButtonClick(button.url)"
            class="w-fit max-w-full py-2! text-start text-heading-4"
            :style="{ width: button.width ? `${button.width}px` : undefined }"
          >
            <template v-if="button.endIcon" #rightIcon>
              <OzIcon :name="button.endIcon" :size="button.endIconSize" />
            </template>
          </OzContainedButton>
        </div>

        <!-- Helper Text with Links -->
        <p
          v-if="helperHtml"
          id="helperText"
          :class="['text-body-small', 'mt-14', isDark ? 'text-light-text-200' : 'text-dark-text-200']"
          v-html="helperHtml"
        />
      </div>
    </div>

    <!-- Access request footer and accessibility settings modal -->
    <div
      class="flex justify-center tablet-portrait:justify-start absolute bottom-12 tablet-portrait:bottom-0 start-0 end-0 tablet-portrait:end-auto"
    >
      <AccessRequestFooter
        :dark-mode="isDark"
        @open-accessibility-settings="accessibilitySettingsStore.openAccessibilitySettingsModal()"
      />
    </div>
    <AccessibilitySettingsModal
      v-if="accessibilitySettingsStore.xAccessibilitySettingsModal"
      :visible="accessibilitySettingsStore.xAccessibilitySettingsModal"
      :portal-selector="'body'"
      :user="currentUser"
      :update-type="'page'"
      :settings-to-show="[AccessibilitySetting.HighContrastMode, AccessibilitySetting.ReducedMotion]"
      :dark-mode="isDark"
      @close="accessibilitySettingsStore.closeAccessibilitySettingsModal"
    />
    <UserMenuPopover
      v-if="xUserMenu && currentUser.registered_at"
      :user="currentUser"
      :anchor="avatarButtonAnchor"
      :dark-mode="isDark"
      @close="hideUserMenu"
    />
  </main>
</template>

<style>
@import '@@/styles/4/inter_subset.css';

:root {
  @apply font-sans;
}

/** To remove the margin around the html body so that the background color covers the entire viewport */
html > body {
  @apply m-0!;
}

h1 {
  @apply font-semibold;
}

p {
  @apply font-normal;
}

[role='dialog'] p,
[role='dialog'] h2 {
  @apply m-0;
}
</style>
<style scoped>
/* Style for anchor tags in helper & description text - using deep selector for v-html content */
.light-theme p:deep(a) {
  @apply underline underline-offset-2;
  @apply focus-visible:ring-2 focus-visible:ring-grape-500 focus-visible:outline-hidden focus-visible:rounded;
}

.light-theme #helperText:deep(a) {
  @apply text-dark-text-200;
}

.light-theme #description:deep(a) {
  @apply text-dark-text-100 font-semibold;
}

.dark-theme p:deep(a) {
  @apply underline underline-offset-2;
  @apply focus-visible:ring-2 focus-visible:ring-canary-500 focus-visible:outline-hidden focus-visible:rounded;
}

.dark-theme #helperText:deep(a) {
  @apply text-light-text-200;
}

.dark-theme #description:deep(a) {
  @apply text-light-text-100 font-semibold;
}
</style>
